import COLORS from "common/Colors";

const BLACK = COLORS.SECONDARY_BLACK;
const RED = COLORS.ERROR;
const ORANGE = COLORS.WARNING;

export const KPI_PLANNING_MINE_STATUS = {
  yettoaccept: { label: "Yet to Accept", color: RED },
  accepted: { label: "Accepted", color: BLACK },
  declined: { label: "Declined", color: BLACK },
  awaitingkpisubmission: { label: "Awaiting your KPI Submission", color: RED },
  // Same underlying planningstatus as KPI_PLANNING_MYTEAM_STATUS.awaitingkpireview -
  // the ReviewKpi.js screen gates its Accept/Request Revision actions on this exact value.
  awaitingkpireview: { label: "Sent for KPI Review", color: BLACK },
  // Same underlying planningstatus as KPI_PLANNING_MYTEAM_STATUS.revisionrequested -
  // set by the backend's "requestrevision" endpoint.
  revisionrequested: { label: "Requested Revision", color: RED },
  awaitingprogressreview: { label: "Awaiting Progress Review", color: RED },
  inprogressreview: { label: "In Progress Review", color: BLACK },
  reviewsclosed: { label: "Reviews Closed", color: BLACK },
};

// Hover actions shown per KPI Planning (Mine) row status.
// type: "link" (text link only), "link+info" (text link + info tooltip icon)
export const KPI_PLANNING_MINE_ACTIONS = {
  yettoaccept: { type: "link", label: "Review & Accept" },
  accepted: {
    type: "link+info",
    label: "View",
    tooltip:
      "This appraisal includes the targets you agreed to achieve for the upcoming period. You can attend this appraisal form once the start date arrives.",
  },
  declined: {
    type: "link+info",
    label: "View",
    tooltip: "The appraiser will resend the contract with the needful changes for you to accept.",
  },
  awaitingkpisubmission: { type: "link", label: "Fill KPI" },
  // Editing while sent for review resubmits (same submitkpi endpoint the
  // initial submission uses) and keeps this same status - the appraiser
  // just sees the updated content whenever they open it.
  awaitingkpireview: { type: "link", label: "View", icons: ["edit", "delete"] },
  revisionrequested: { type: "link", label: "Revise KPI", icons: ["delete"] },
  awaitingprogressreview: { type: "link", label: "View" },
  inprogressreview: { type: "link", label: "View" },
  reviewsclosed: { type: "link", label: "View" },
};

export const KPI_PLANNING_MYTEAM_STATUS = {
  notyetcreated: { label: "Not Yet Created", color: RED },
  draft: { label: "Draft", color: BLACK },
  // Same underlying planningstatus as KPI_PLANNING_MINE_STATUS.yettoaccept -
  // set by the backend's initial "contract" insert.
  yettoaccept: { label: "Waiting for Acceptance", color: BLACK },
  accepted: { label: "Accepted", color: BLACK },
  declined: { label: "Declined", color: ORANGE },
  // Same underlying planningstatus as KPI_PLANNING_MINE_STATUS.awaitingkpisubmission -
  // this is the My Team side's label for that same record.
  awaitingkpisubmission: { label: "Awaiting Appraisee KPI Submission", color: BLACK },
  awaitingkpireview: { label: "Awaiting KPI Review", color: RED },
  revisionrequested: { label: "Revision Requested", color: BLACK },
  awaitingprogressreview: { label: "Awaiting Progress Review", color: RED },
  inprogressreview: { label: "In Progress Review", color: BLACK },
  reviewsclosed: { label: "Reviews Closed", color: BLACK },
};

// Hover actions shown per KPI Planning (My Team) row status.
// type: "link" (text link only), "link+icons" (text link + icon buttons),
// "icons" (icon buttons only)
export const KPI_PLANNING_MYTEAM_ACTIONS = {
  notyetcreated: { type: "link", label: "Create KPI" },
  draft: { type: "link", label: "Complete" },
  yettoaccept: { type: "icons", icons: ["edit", "delete"] },
  accepted: { type: "icons", icons: ["calendar", "delete"] },
  declined: { type: "icons", icons: ["edit", "delete"] },
  awaitingkpireview: { type: "link+icons", label: "Review KPI", icons: ["calendar"] },
  revisionrequested: { type: "icons", icons: ["calendar", "delete"] },
  awaitingprogressreview: { type: "link+icons", label: "Review Progress", icons: ["calendar", "delete"] },
  inprogressreview: { type: "icons", icons: ["calendar", "delete"] },
  reviewsclosed: { type: "icons", icons: ["calendar", "delete"] },
};

export const APPRAISAL_MINE_STATUS = {
  notopen: { label: "Not Open", color: BLACK },
  yettoattend: { label: "Yet to Attend", color: RED },
  // Same underlying appraisalstatus as APPRAISAL_MYTEAM_STATUS.yettoreview -
  // this is the Mine side's label for that same record.
  yettoreview: { label: "Appraiser Review Pending", color: BLACK },
  // Same underlying appraisalstatus as APPRAISAL_MYTEAM_STATUS.closed -
  // this is the Mine side's label for that same record.
  closed: { label: "Appraiser Review Completed", color: BLACK },
};

export const APPRAISAL_MYTEAM_STATUS = {
  // Same underlying appraisalstatus as APPRAISAL_MINE_STATUS.notopen/yettoattend -
  // the My Team side doesn't distinguish between them, both just mean the
  // appraisee hasn't submitted their self-assessment yet.
  notopen: { label: "Awaiting Appraisee Response", color: BLACK },
  yettoattend: { label: "Awaiting Appraisee Response", color: BLACK },
  yettoreview: { label: "Yet to Review", color: RED },
  closed: { label: "Closed", color: BLACK },
};

// Hover actions shown per Appraisal (Mine) row status.
// type: "info" (tooltip icon only), "link" (icon + text link)
export const APPRAISAL_MINE_ACTIONS = {
  notopen: {
    type: "info",
    tooltip:
      "You have accepted the contract, but the appraisal period has not started yet. You'll be able to submit your self-appraisal once the appraisal window opens.",
  },
  yettoattend: { type: "link", icon: "export", label: "Attend" },
  yettoreview: { type: "link", icon: "eye", label: "View" },
  closed: { type: "link", icon: "eye", label: "View", extraIcon: "chart" },
};

// Hover actions shown per Appraisal (My Team) row status.
// type: "icons" (icon buttons only), "link+icons" (icon buttons + text link)
export const APPRAISAL_MYTEAM_ACTIONS = {
  notopen: { type: "icons", icons: ["calendar", "delete"] },
  yettoattend: { type: "icons", icons: ["calendar", "delete"] },
  yettoreview: { type: "link+icons", label: "Review", icons: ["delete"] },
  closed: { type: "icons", icons: ["chart", "delete"] },
};

export const statusMeta = (map, status) =>
  map[status] || { label: status || "-", color: BLACK };
